/**
 * Toast Notification System
 *
 * Provides non-blocking toast notifications to replace alert() calls.
 * Supports success, error, warning, and info toast types.
 *
 * Usage:
 * ```ts
 * import { useToast } from '@/composables/useToast';
 *
 * const toast = useToast();
 * toast.success('Item saved successfully');
 * toast.error('Failed to save item');
 * toast.warning('This action cannot be undone');
 * toast.info('Processing your request...');
 * ```
 */

import { ref, readonly } from 'vue';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  title?: string;
  duration: number;
  persistent: boolean;
  createdAt: number;
}

export interface ToastOptions {
  title?: string;
  duration?: number;
  persistent?: boolean;
}

// Default durations in milliseconds
const DEFAULT_DURATIONS: Record<ToastType, number> = {
  success: 3000,
  info: 4000,
  warning: 5000,
  error: 6000,
};

// Global toast state (singleton pattern)
const toasts = ref<Toast[]>([]);
let toastIdCounter = 0;

/**
 * Generate a unique toast ID
 */
function generateId(): string {
  return `toast-${++toastIdCounter}-${Date.now()}`;
}

/**
 * Add a toast to the queue
 */
function addToast(type: ToastType, message: string, options: ToastOptions = {}): string {
  const id = generateId();
  const duration = options.duration ?? DEFAULT_DURATIONS[type];

  const toast: Toast = {
    id,
    type,
    message,
    title: options.title,
    duration,
    persistent: options.persistent ?? false,
    createdAt: Date.now(),
  };

  toasts.value.push(toast);

  // Auto-remove non-persistent toasts after duration
  if (!toast.persistent && duration > 0) {
    setTimeout(() => {
      removeToast(id);
    }, duration);
  }

  return id;
}

/**
 * Remove a toast by ID
 */
function removeToast(id: string): void {
  const index = toasts.value.findIndex(t => t.id === id);
  if (index !== -1) {
    toasts.value.splice(index, 1);
  }
}

/**
 * Remove all toasts
 */
function clearAllToasts(): void {
  toasts.value = [];
}

/**
 * Toast notification composable
 */
export function useToast() {
  return {
    /**
     * All active toasts (readonly)
     */
    toasts: readonly(toasts),

    /**
     * Show a success toast
     */
    success(message: string, options?: ToastOptions): string {
      return addToast('success', message, options);
    },

    /**
     * Show an error toast
     */
    error(message: string, options?: ToastOptions): string {
      // Errors persist longer by default and can be set to persistent
      return addToast('error', message, {
        persistent: false,
        ...options,
      });
    },

    /**
     * Show a warning toast
     */
    warning(message: string, options?: ToastOptions): string {
      return addToast('warning', message, options);
    },

    /**
     * Show an info toast
     */
    info(message: string, options?: ToastOptions): string {
      return addToast('info', message, options);
    },

    /**
     * Show a toast with custom type
     */
    show(type: ToastType, message: string, options?: ToastOptions): string {
      return addToast(type, message, options);
    },

    /**
     * Remove a specific toast by ID
     */
    remove: removeToast,

    /**
     * Clear all toasts
     */
    clearAll: clearAllToasts,
  };
}

// Export singleton instance for direct import
export const toast = useToast();
